import { useState, useRef } from "react";
import Cropper, { ReactCropperElement } from "react-cropper";
import "cropperjs/dist/cropper.css";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { RotateCw, FlipHorizontal, ZoomIn, ZoomOut, Check } from "lucide-react";

interface ImageCropDialogProps {
  open: boolean;
  onClose: () => void;
  imageSrc: string;
  aspectRatio?: number;
  onCropped: (file: File) => void;
}

export function ImageCropDialog({
  open,
  onClose,
  imageSrc,
  aspectRatio,
  onCropped,
}: ImageCropDialogProps) {
  const cropperRef = useRef<ReactCropperElement>(null);
  const [saving, setSaving] = useState(false);

  const handleCrop = () => {
    const cropper = cropperRef.current?.cropper;
    if (!cropper) return;

    setSaving(true);

    try {
      const canvas = cropper.getCroppedCanvas({
        maxWidth: 1200,
        maxHeight: 1200,
        imageSmoothingQuality: "high",
      });

      if (!canvas) {
        setSaving(false);
        return;
      }

      canvas.toBlob(
        (blob) => {
          setSaving(false);
          if (!blob) return;
          const file = new File([blob], "cropped.jpg", { type: "image/jpeg" });
          // İki adım, ayrı microtask'larda:
          //   1) onClose() → React commit → cropperjs unmount + DOM temiz
          //   2) (microtask sonrası) onCropped(file) → parent mutation
          //
          // queueMicrotask cropperjs cleanup'ının (useEffect) bitmesini
          // garanti eder. Aynı tick'te peş peşe çağırsak React batch'leri
          // commit edebilir ama nested dialog kullanımlarında (örn. banner
          // formu içinde crop dialog) outer modal hâlâ canlıyken cropperjs
          // child DOM'unu silmeye çalışırken parent re-render arasında
          // "removeChild ... not a child of this node" race çıkıyordu.
          onClose();
          queueMicrotask(() => onCropped(file));
        },
        "image/jpeg",
        0.9
      );
    } catch {
      setSaving(false);
    }
  };

  const rotate = () => cropperRef.current?.cropper.rotate(90);
  const flipH = () => {
    const c = cropperRef.current?.cropper;
    if (c) c.scaleX(-(c.getData().scaleX || 1));
  };
  const zoomIn = () => cropperRef.current?.cropper.zoom(0.1);
  const zoomOut = () => cropperRef.current?.cropper.zoom(-0.1);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !saving) onClose(); }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Gorseli Duzenle</DialogTitle>
        </DialogHeader>

        <div className="overflow-hidden rounded-lg bg-muted" style={{ maxHeight: 400 }}>
          {open && imageSrc && (
            <Cropper
              ref={cropperRef}
              src={imageSrc}
              style={{ height: 350, width: "100%" }}
              aspectRatio={aspectRatio}
              viewMode={1}
              guides
              background={false}
              responsive
              autoCropArea={0.9}
              checkOrientation={false}
            />
          )}
        </div>

        <div className="flex items-center justify-between">
          <div className="flex gap-1">
            <Button type="button" variant="outline" size="icon" onClick={rotate} title="Dondur">
              <RotateCw className="size-4" />
            </Button>
            <Button type="button" variant="outline" size="icon" onClick={flipH} title="Cevir">
              <FlipHorizontal className="size-4" />
            </Button>
            <Button type="button" variant="outline" size="icon" onClick={zoomIn} title="Yakinlastir">
              <ZoomIn className="size-4" />
            </Button>
            <Button type="button" variant="outline" size="icon" onClick={zoomOut} title="Uzaklastir">
              <ZoomOut className="size-4" />
            </Button>
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={saving}>Iptal</Button>
            <Button type="button" onClick={handleCrop} disabled={saving}>
              <Check className="mr-2 size-4" />
              {saving ? "Isleniyor..." : "Uygula ve Yukle"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
